import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';

/** The phone Control Panel's status tabs, as the server's list filter
 *  labels. 'All' and Archived are desktop only: search looks across every
 *  status instead. */
export const MOBILE_STATUS_FILTERS = ['In progress', 'Pending', 'Complete'] as const;
export type MobileStatusFilter = typeof MOBILE_STATUS_FILTERS[number];
export const DEFAULT_STATUS_FILTER: MobileStatusFilter = 'In progress';

/** A saved filter the phone does not offer (All, Archived, anything else)
 *  becomes In progress. */
export const coerceStatusFilter = (value: string | null | undefined): MobileStatusFilter =>
  (MOBILE_STATUS_FILTERS as readonly string[]).includes(value ?? '')
    ? (value as MobileStatusFilter)
    : DEFAULT_STATUS_FILTER;

export interface StatusFilterBarProps {
  value: MobileStatusFilter;
  onChange: (value: MobileStatusFilter) => void;
  /** The selected tab's pour count, shown beside its label once loaded. */
  count?: number | null;
  /** The selected tab was tapped again: the page refreshes its list. */
  onReselect?: () => void;
}

/**
 * A segmented control (In progress | Pending | Completed): one rounded grey
 * track, the selected tab a white pill on it (the tab indicator, stretched
 * to full height). The selected tab carries its pour count as a green
 * badge; tapping it again refreshes the list.
 */
const StatusFilterBar: React.FC<StatusFilterBarProps> = ({ value, onChange, count, onReselect }) => {
  const { t } = useTranslation();
  const labels: Record<MobileStatusFilter, string> = {
    'In progress': t('pours.inProgress', 'In Progress'),
    Pending: t('dashboard.pending', 'Pending'),
    Complete: t('pours.complete', 'Completed'),
  };

  return (
    <Tabs
      value={value}
      onChange={(_event, next: MobileStatusFilter) => onChange(next)}
      variant="fullWidth"
      aria-label={t('pours.statusFilter', 'Status Filter')}
      slotProps={{
        indicator: {
          sx: { height: '100%', borderRadius: '9px', bgcolor: 'common.white', boxShadow: '0 1px 3px rgba(0,0,0,.12)', zIndex: 0 },
        },
      }}
      sx={{
        minHeight: 44,
        p: '3px',
        borderRadius: '12px',
        bgcolor: '#e3e6ea',
        '& .MuiTabs-flexContainer': { position: 'relative', zIndex: 1 },
        '& .MuiTab-root': {
          // Share the track by label width, so "In Progress 50" is never cut.
          flex: '1 1 auto',
          minHeight: 38,
          minWidth: 0,
          px: 1,
          py: 0,
          borderRadius: '9px',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'text.secondary',
          zIndex: 1,
        },
        '& .MuiTab-root.Mui-selected': { color: 'text.primary', fontWeight: 600 },
      }}
    >
      {MOBILE_STATUS_FILTERS.map((filter) => (
        <Tab
          key={filter}
          value={filter}
          onClick={() => {
            if (filter === value) onReselect?.();
          }}
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              {labels[filter]}
              {filter === value && count != null && (
                <>
                  {/* A space for the accessible name ("In Progress 50"); the
                      flex gap does the visual spacing. */}
                  {' '}
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      lineHeight: 1.5,
                      px: '7px',
                      borderRadius: '10px',
                      bgcolor: 'primary.main',
                      color: 'common.white',
                    }}
                  >
                    {count}
                  </Box>
                </>
              )}
            </Box>
          }
        />
      ))}
    </Tabs>
  );
};

export default StatusFilterBar;
